import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type ValueType = 'string' | 'number' | 'boolean';

type UseSearchParamFilterOptions<T> = {
  valueType: ValueType;
  paramName: string;
  defaultValue?: T;
  deleteParamIfEmpty?: boolean;
};

type UseSearchParamFilterReturn<T> = {
  value: T | undefined;
  onChange: (newValue: T) => void;
};

export function useSearchParamFilter<T extends string | number | boolean>({
  paramName,
  defaultValue,
  valueType = 'string',
  deleteParamIfEmpty = false,
}: UseSearchParamFilterOptions<T>): UseSearchParamFilterReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo<T | undefined>(() => {
    const rawValue = searchParams.get(paramName);
    if (rawValue === null) return defaultValue;

    switch (valueType) {
      case 'number': {
        const parsed = Number(rawValue);
        return isNaN(parsed) ? defaultValue : (parsed as T);
      }
      case 'boolean': {
        if (rawValue === 'true') return true as T;
        if (rawValue === 'false') return false as T;
        return defaultValue;
      }
      case 'string':
      default:
        return rawValue as T;
    }
  }, [searchParams, paramName, defaultValue, valueType]);

  const onChange = useCallback(
    (newValue: T) => {
      if (
        deleteParamIfEmpty &&
        (newValue === undefined || newValue === null || newValue === '')
      ) {
        searchParams.delete(paramName);
      } else {
        searchParams.set(paramName, String(newValue));
      }
      setSearchParams(searchParams);
    },
    [paramName, searchParams, setSearchParams, deleteParamIfEmpty]
  );

  return { value, onChange };
}
